// Simple JSON-based database for user progress
// This can be easily replaced with a real database later

const DB_KEY = 'arabic_learning_progress_db';

// Initialize database if it doesn't exist
function initializeDatabase() {
  const existing = localStorage.getItem(DB_KEY);
  if (!existing) {
    localStorage.setItem(DB_KEY, JSON.stringify({}));
  }
}

// Get all users' progress data
export function getAllProgressData() {
  initializeDatabase();
  try {
    const data = localStorage.getItem(DB_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error reading database:', error);
    return {};
  }
}

// Get progress for a specific user
export function getUserProgress(userId) {
  const allData = getAllProgressData();
  
  // Only log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('Getting progress for user:', userId);
    console.log('Available users in database:', Object.keys(allData));
  }
  
  return allData[userId] || {
    alphabet: 0,
    words: 0,
    numbers: 0,
    days: 0,
    colors: 0,
    animals: 0,
    conversation: 0,
    alphabetLetters: [],
    lastUpdated: null
  };
}

// Save progress for a specific user
export function saveUserProgress(userId, progressData) {
  try {
    const allData = getAllProgressData();
    const existingProgress = allData[userId] || {};
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Saving progress for user:', userId);
      console.log('Existing progress:', existingProgress);
      console.log('New progress data:', progressData);
    }
    
    // Merge with existing progress
    const updatedProgress = {
      ...existingProgress,
      ...progressData,
      lastUpdated: new Date().toISOString()
    };
    
    allData[userId] = updatedProgress;
    localStorage.setItem(DB_KEY, JSON.stringify(allData));
    
    if (process.env.NODE_ENV === 'development') {
      console.log('Progress saved to database:', { userId, progressData });
    }
    return updatedProgress;
  } catch (error) {
    console.error('Error saving progress to database:', error);
    throw error;
  }
}

// Update specific lesson progress
export function updateLessonProgress(userId, lessonType, progressValue) {
  const currentProgress = getUserProgress(userId);
  
  let updatedProgress;
  if (lessonType === 'alphabet' && Array.isArray(progressValue)) {
    // For alphabet, we store the array of learned letters
    updatedProgress = {
      ...currentProgress,
      alphabetLetters: progressValue,
      alphabet: progressValue.length
    };
  } else {
    // For other lessons, we store the count
    updatedProgress = {
      ...currentProgress,
      [lessonType]: progressValue
    };
  }
  
  return saveUserProgress(userId, updatedProgress);
}

// Get progress summary for dashboard
export function getProgressSummary(userId) {
  const progress = getUserProgress(userId);
  return {
    alphabet: progress.alphabet || 0,
    words: progress.words || 0,
    numbers: progress.numbers || 0,
    days: progress.days || 0,
    colors: progress.colors || 0,
    animals: progress.animals || 0,
    conversation: progress.conversation || 0
  };
}

// Clear all progress for a user (for testing)
export function clearUserProgress(userId) {
  const allData = getAllProgressData();
  delete allData[userId];
  localStorage.setItem(DB_KEY, JSON.stringify(allData));
}

// Get all user IDs in the database
export function getAllUserIds() {
  const allData = getAllProgressData();
  return Object.keys(allData);
}

// Migrate progress from one user ID to another
export function migrateUserProgress(fromUserId, toUserId) {
  const allData = getAllProgressData();
  if (allData[fromUserId]) {
    allData[toUserId] = {
      ...allData[fromUserId],
      lastUpdated: new Date().toISOString()
    };
    delete allData[fromUserId];
    localStorage.setItem(DB_KEY, JSON.stringify(allData));
    console.log(`Progress migrated from ${fromUserId} to ${toUserId}`);
    return true;
  }
  return false;
}

// Find progress by email (if stored)
export function findProgressByEmail(email) {
  const allData = getAllProgressData();
  for (const [userId, progress] of Object.entries(allData)) {
    if (progress.email === email) {
      return { userId, progress };
    }
  }
  return null;
}

// Save user email with progress for easier identification
export function saveUserProgressWithEmail(userId, progressData, email) {
  const progressWithEmail = {
    ...progressData,
    email: email
  };
  return saveUserProgress(userId, progressWithEmail);
}
